// GlobalSearchOverlay — Cmd/Ctrl+K-style command palette for the student
// portal. Centered modal with a magnifying-glass input on top and grouped
// results beneath: jump-to (programs the student is on), suggestions
// (open competitions to join), deliverables (assignments due across all
// registered programs), announcements, and recent activity.
//
// Empty-state (no query) shows just JUMP TO + SUGGESTED so the panel
// reads as a "go anywhere" surface rather than a list of nothing. As
// soon as the user types, every group narrows to text-matching rows
// across name / title / body / company.

import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { router } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import Svg, { Circle, Path, Rect } from "react-native-svg";

import {
  listMyAnnouncements,
  type MyAnnouncement,
} from "../../lib/announcements";
import { listMyNotifications } from "../../lib/notifications";
import {
  listActiveRegisteredCompetitions,
  listPreReleaseCompetitions,
} from "../../lib/studentDashboard";
import { listOpenCasesForStudent } from "../../lib/studentCases";
import { competitionTier, competitionTierLabel } from "../../lib/studentCases";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../lib/authContext";
import { getMyStudent } from "../../lib/students";
import type {
  Deliverable,
  NotificationRow,
  Student,
  StudentCaseRow,
} from "../../types/database";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const AMBER_DEEP = COLORS.AMBER_DEEP.hex;
const SAND_DARK = "#D9D2C5";
const SAND_LINE = COLORS.SAND_LINE.hex;
const LINEN = COLORS.LINEN.hex;
const TEXT = "#1a1a18";
const TEXT_MUTED = "rgba(28,43,26,0.62)";
const TEXT_LIGHT = "rgba(28,43,26,0.42)";

type DeliverableSearchRow = Deliverable & {
  competition_name: string;
  company_name: string | null;
};

type SearchData = {
  myPrograms: StudentCaseRow[];
  openPrograms: StudentCaseRow[];
  deliverables: DeliverableSearchRow[];
  announcements: MyAnnouncement[];
  activity: NotificationRow[];
};

const EMPTY_DATA: SearchData = {
  myPrograms: [],
  openPrograms: [],
  deliverables: [],
  announcements: [],
  activity: [],
};

export function GlobalSearchOverlay({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const isWeb = Platform.OS === "web";
  const { user } = useAuth();

  const [query, setQuery] = useState("");
  const [data, setData] = useState<SearchData>(EMPTY_DATA);
  const inputRef = useRef<TextInput | null>(null);
  const loadedForUserRef = useRef<string | null>(null);

  // Lazy-load the search index the first time the overlay opens for this
  // user. Keep it cached for subsequent opens — these queries are cheap
  // but there's no need to rerun them every time the user hits Cmd+K.
  const load = useCallback(async () => {
    if (!user) return;
    if (loadedForUserRef.current === user.id) return;
    loadedForUserRef.current = user.id;

    const [
      myPrograms,
      student,
      announcements,
      activity,
    ]: [StudentCaseRow[], Student | null, MyAnnouncement[], NotificationRow[]] =
      await Promise.all([
        listActiveRegisteredCompetitions(user.id),
        getMyStudent(),
        listMyAnnouncements(),
        listMyNotifications(40),
      ]);

    // Open programs the student is eligible for and not yet on.
    const exclude = myPrograms.map((p) => p.id);
    const browse = await listOpenCasesForStudent(student, exclude).catch(() => ({
      active: [] as StudentCaseRow[],
      soon: [] as StudentCaseRow[],
    }));
    const preRelease = await listPreReleaseCompetitions().catch(
      () => [] as StudentCaseRow[]
    );
    const openPrograms = [...browse.active, ...browse.soon, ...preRelease].slice(0, 30);

    // Pull deliverables for every competition the student is registered for —
    // those are the only "assignments" they can act on. We surface name +
    // due date + competition title so users can jump straight to a row.
    let deliverables: DeliverableSearchRow[] = [];
    if (myPrograms.length > 0) {
      const compIds = myPrograms.map((p) => p.id);
      const { data: dels } = await supabase
        .from("deliverables")
        .select("id, competition_id, sequence, name, description, file_type, rubric, point_value, is_scored, is_locked_gate, allow_revisions, opens_at, due_at, template_url")
        .in("competition_id", compIds)
        .order("due_at", { ascending: true });
      const titleByComp = new Map<string, { name: string; company: string | null }>();
      for (const p of myPrograms) {
        titleByComp.set(p.id, { name: p.name, company: p.company_name ?? null });
      }
      deliverables = (dels ?? []).map((d) => ({
        ...(d as unknown as Deliverable),
        competition_name: titleByComp.get(d.competition_id as string)?.name ?? "Program",
        company_name: titleByComp.get(d.competition_id as string)?.company ?? null,
      }));
    }

    setData({
      myPrograms,
      openPrograms,
      deliverables,
      announcements,
      activity,
    });
  }, [user]);

  useEffect(() => {
    if (visible) void load();
  }, [visible, load]);

  // Web: focus the input on open + close on ESC + open with Cmd/Ctrl+K from
  // anywhere. The Cmd+K binding lives on `window` so any focused element
  // can summon the palette.
  useEffect(() => {
    if (!visible || !isWeb) return;
    const id = setTimeout(() => inputRef.current?.focus(), 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(id);
      window.removeEventListener("keydown", onKey);
    };
  }, [visible, isWeb, onClose]);

  // When closed, clear the query so the panel reopens fresh.
  useEffect(() => {
    if (!visible) setQuery("");
  }, [visible]);

  const filtered = useMemo(() => filterAll(data, query), [data, query]);

  const goto = useCallback(
    (path: string) => {
      onClose();
      // Defer the navigation so the modal animates out cleanly first.
      setTimeout(() => router.push(path as Href), 30);
    },
    [onClose]
  );

  if (!visible) return null;

  const hasQuery = query.trim().length > 0;

  return (
    <RNModal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        accessibilityLabel="Close search"
        style={[
          {
            flex: 1,
            backgroundColor: "rgba(28,43,26,0.45)",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingTop: 84,
            paddingHorizontal: 20,
          },
          isWeb
            ? ({
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                cursor: "default",
              } as object)
            : null,
        ]}
      >
        <Pressable
          onPress={(e) => e.stopPropagation?.()}
          style={[
            {
              width: "100%",
              maxWidth: 720,
              maxHeight: "78vh" as unknown as number,
              backgroundColor: LINEN,
              borderRadius: 14,
              overflow: "hidden",
              borderWidth: 1,
              borderColor: "rgba(28,43,26,0.10)",
            },
            isWeb
              ? ({
                  boxShadow:
                    "0 30px 60px -20px rgba(28,43,26,0.45), 0 6px 16px -8px rgba(28,43,26,0.18)",
                  cursor: "default",
                } as object)
              : null,
          ]}
        >
          {/* Search bar */}
          <View
            style={{
              paddingHorizontal: 22,
              paddingVertical: 16,
              borderBottomWidth: 1,
              borderBottomColor: SAND_LINE,
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}
          >
            <SearchGlyph color={TEXT_LIGHT} />
            <TextInput
              ref={inputRef}
              value={query}
              onChangeText={setQuery}
              placeholder="Search competitions, programs, or teammates…"
              placeholderTextColor={TEXT_LIGHT}
              autoFocus
              style={[
                {
                  flex: 1,
                  fontFamily: "DMSans_400Regular",
                  fontSize: 16,
                  color: TEXT,
                  paddingVertical: 4,
                },
                isWeb ? ({ outlineStyle: "none" } as unknown as object) : null,
              ]}
            />
            <KbdHint label="ESC" />
          </View>

          {/* Results */}
          <ScrollView
            style={{ maxHeight: 540 }}
            contentContainerStyle={{ paddingVertical: 6 }}
            keyboardShouldPersistTaps="handled"
          >
            {/* Empty state — no query yet, no data loaded yet. */}
            {!hasQuery &&
            data.myPrograms.length === 0 &&
            data.openPrograms.length === 0 ? (
              <View style={{ paddingVertical: 36, alignItems: "center" }}>
                <Text
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 13,
                    color: TEXT_MUTED,
                  }}
                >
                  Type to search programs, deliverables, announcements, or activity.
                </Text>
              </View>
            ) : null}

            {/* Jump-to · my programs (always shown when present, even with a query) */}
            {filtered.myPrograms.length > 0 ? (
              <Group title={hasQuery ? "My programs" : "Jump to · My programs"}>
                {filtered.myPrograms.map((p) => (
                  <ProgramRow
                    key={p.id}
                    title={p.name}
                    sub={subForProgram(p)}
                    icon={<TileGlyph initial={(p.company_name?.[0] ?? p.name[0] ?? "?").toUpperCase()} />}
                    onPress={() => goto(`/student/competition/${p.id}`)}
                  />
                ))}
              </Group>
            ) : null}

            {/* Suggested · find competitions */}
            {filtered.openPrograms.length > 0 ? (
              <Group title={hasQuery ? "Open competitions" : "Suggested · Find competitions"}>
                {filtered.openPrograms.slice(0, 8).map((p) => (
                  <ProgramRow
                    key={p.id}
                    title={p.name}
                    sub={subForProgram(p)}
                    icon={<TrophyGlyph />}
                    onPress={() => goto(`/student/find/competition/${p.id}`)}
                  />
                ))}
              </Group>
            ) : null}

            {/* Deliverables (when matching) */}
            {filtered.deliverables.length > 0 ? (
              <Group title="Deliverables">
                {filtered.deliverables.slice(0, 8).map((d) => (
                  <ProgramRow
                    key={d.id}
                    title={`${d.sequence}. ${d.name}`}
                    sub={subForDeliverable(d)}
                    icon={<DocumentGlyph />}
                    onPress={() =>
                      goto(`/student/competition/${d.competition_id}?tab=deliverables&deliverable=${d.id}`)
                    }
                  />
                ))}
              </Group>
            ) : null}

            {/* Announcements (when matching) */}
            {filtered.announcements.length > 0 ? (
              <Group title="Announcements">
                {filtered.announcements.slice(0, 6).map((a) => (
                  <ProgramRow
                    key={a.id}
                    title={a.title}
                    sub={subForAnnouncement(a)}
                    icon={<MegaphoneGlyph />}
                    onPress={() => {
                      // No deep link to a single announcement detail — drop
                      // the user on the activity page where they can expand
                      // it inline.
                      goto(`/student/notifications`);
                    }}
                  />
                ))}
              </Group>
            ) : null}

            {/* Recent activity (when matching) */}
            {filtered.activity.length > 0 ? (
              <Group title="Activity">
                {filtered.activity.slice(0, 6).map((n) => (
                  <ProgramRow
                    key={n.id}
                    title={n.title}
                    sub={n.body ?? "Notification"}
                    icon={<BellGlyph />}
                    onPress={() => {
                      if (n.link_path) {
                        goto(n.link_path);
                      } else {
                        goto(`/student/notifications`);
                      }
                    }}
                  />
                ))}
              </Group>
            ) : null}

            {/* No-match state */}
            {hasQuery &&
            filtered.myPrograms.length === 0 &&
            filtered.openPrograms.length === 0 &&
            filtered.deliverables.length === 0 &&
            filtered.announcements.length === 0 &&
            filtered.activity.length === 0 ? (
              <View style={{ paddingVertical: 36, alignItems: "center", paddingHorizontal: 24 }}>
                <Text
                  style={{
                    fontFamily: "DMSans_500Medium",
                    fontSize: 13,
                    color: TEXT_MUTED,
                    textAlign: "center",
                  }}
                >
                  No matches for “{query.trim()}”
                </Text>
                <Text
                  style={{
                    marginTop: 6,
                    fontFamily: "DMSans_400Regular",
                    fontSize: 12,
                    color: TEXT_LIGHT,
                    textAlign: "center",
                  }}
                >
                  Try a sponsor name, a deliverable, or a teammate.
                </Text>
              </View>
            ) : null}
          </ScrollView>
        </Pressable>
      </Pressable>
    </RNModal>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Filter logic — single text query applied across every category.
// ────────────────────────────────────────────────────────────────────────────

function filterAll(data: SearchData, raw: string): SearchData {
  const q = raw.trim().toLowerCase();
  if (!q) {
    return {
      ...data,
      // Empty-query view: skip deliverables / announcements / activity. The
      // "jump to" + "suggested" sections carry the panel until the user
      // actually types something.
      deliverables: [],
      announcements: [],
      activity: [],
    };
  }
  const programMatch = (p: StudentCaseRow) =>
    (p.name ?? "").toLowerCase().includes(q) ||
    (p.company_name ?? "").toLowerCase().includes(q);
  return {
    myPrograms: data.myPrograms.filter(programMatch),
    openPrograms: data.openPrograms.filter(programMatch),
    deliverables: data.deliverables.filter(
      (d) =>
        (d.name ?? "").toLowerCase().includes(q) ||
        (d.competition_name ?? "").toLowerCase().includes(q) ||
        (d.company_name ?? "").toLowerCase().includes(q)
    ),
    announcements: data.announcements.filter(
      (a) =>
        (a.title ?? "").toLowerCase().includes(q) ||
        (a.body ?? "").toLowerCase().includes(q)
    ),
    activity: data.activity.filter(
      (n) =>
        (n.title ?? "").toLowerCase().includes(q) ||
        (n.body ?? "").toLowerCase().includes(q)
    ),
  };
}

function subForProgram(p: StudentCaseRow): string {
  const tierLabel = competitionTierLabel(competitionTier(p));
  const parts: string[] = [];
  if (p.company_name) parts.push(p.company_name);
  parts.push(tierLabel);
  return parts.join("  ·  ");
}

function subForDeliverable(d: DeliverableSearchRow): string {
  const parts: string[] = [d.competition_name];
  if (d.due_at) {
    const date = new Date(d.due_at);
    if (!Number.isNaN(date.getTime())) {
      parts.push(`Due ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`);
    }
  }
  return parts.join("  ·  ");
}

function subForAnnouncement(a: MyAnnouncement): string {
  if (a.body && a.body.trim().length > 0) {
    return a.body.trim().slice(0, 110);
  }
  if (a.published_at) {
    return new Date(a.published_at).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  }
  return "Announcement";
}

// ────────────────────────────────────────────────────────────────────────────
// Row + group primitives
// ────────────────────────────────────────────────────────────────────────────

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={{ marginTop: 6 }}>
      <View
        style={{
          paddingHorizontal: 22,
          paddingTop: 14,
          paddingBottom: 6,
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 9.5,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: TEXT_LIGHT,
          }}
        >
          {title}
        </Text>
      </View>
      {children}
    </View>
  );
}

function ProgramRow({
  title,
  sub,
  icon,
  onPress,
}: {
  title: string;
  sub: string;
  icon: React.ReactNode;
  onPress: () => void;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={title}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          paddingHorizontal: 22,
          paddingVertical: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 14,
          backgroundColor: hovered && isWeb ? "rgba(28,43,26,0.04)" : "transparent",
          borderTopWidth: 1,
          borderTopColor: SAND_LINE,
        },
        isWeb ? ({ cursor: "pointer", transition: "background-color 0.12s ease" } as object) : null,
      ]}
    >
      <View style={{ width: 36, alignItems: "center", justifyContent: "center" }}>{icon}</View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          numberOfLines={1}
          style={{
            fontFamily: "CormorantGaramond_500Medium",
            fontSize: 17,
            lineHeight: 22,
            color: TEXT,
          }}
        >
          {title}
        </Text>
        <Text
          numberOfLines={1}
          style={{
            marginTop: 2,
            fontFamily: "DMSans_400Regular",
            fontSize: 12,
            color: TEXT_MUTED,
          }}
        >
          {sub}
        </Text>
      </View>
      <Svg width={13} height={13} viewBox="0 0 12 12" fill="none">
        <Path
          d="M2.5 6h7M6.5 3l3 3-3 3"
          stroke={TEXT_LIGHT}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
    </Pressable>
  );
}

function KbdHint({ label }: { label: string }) {
  return (
    <View
      style={{
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: SAND_DARK,
        backgroundColor: COLORS.WHITE.hex,
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 9.5,
          letterSpacing: 1.4,
          color: TEXT_LIGHT,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Glyphs
// ────────────────────────────────────────────────────────────────────────────

function SearchGlyph({ color }: { color: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      <Circle cx={8} cy={8} r={5.5} stroke={color} strokeWidth={1.5} />
      <Path d="M12.5 12.5 16 16" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    </Svg>
  );
}

function TileGlyph({ initial }: { initial: string }) {
  return (
    <View
      style={{
        width: 30,
        height: 30,
        borderRadius: 6,
        backgroundColor: FOREST,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text
        style={{
          fontFamily: "CormorantGaramond_600SemiBold",
          fontSize: 14,
          color: AMBER,
        }}
      >
        {initial}
      </Text>
    </View>
  );
}

function TrophyGlyph() {
  return (
    <View
      style={{
        width: 30,
        height: 30,
        borderRadius: 6,
        backgroundColor: "rgba(181,135,58,0.14)",
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.30)",
      }}
    >
      <Svg width={16} height={16} viewBox="0 0 16 16" fill="none">
        <Path
          d="M5 3.5h6V7c0 1.7-1.3 3-3 3s-3-1.3-3-3V3.5Z"
          stroke={AMBER_DEEP}
          strokeWidth={1.4}
          strokeLinejoin="round"
          fill="none"
        />
        <Path
          d="M5 5H3.5C3.5 6.5 4.2 7.5 5.4 7.8M11 5h1.5c0 1.5-.7 2.5-1.9 2.8M6 13h4M8 10v3"
          stroke={AMBER_DEEP}
          strokeWidth={1.4}
          strokeLinecap="round"
          fill="none"
        />
      </Svg>
    </View>
  );
}

function DocumentGlyph() {
  return (
    <View
      style={{
        width: 30,
        height: 30,
        borderRadius: 6,
        backgroundColor: "rgba(28,43,26,0.06)",
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: "rgba(28,43,26,0.10)",
      }}
    >
      <Svg width={15} height={15} viewBox="0 0 16 16" fill="none">
        <Path
          d="M4 2.5h5l3 3v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1Z"
          stroke={TEXT_MUTED}
          strokeWidth={1.4}
          strokeLinejoin="round"
          fill="none"
        />
        <Path d="M9 2.5V5.5h3" stroke={TEXT_MUTED} strokeWidth={1.4} strokeLinejoin="round" />
      </Svg>
    </View>
  );
}

function MegaphoneGlyph() {
  return (
    <View
      style={{
        width: 30,
        height: 30,
        borderRadius: 6,
        backgroundColor: "rgba(181,135,58,0.14)",
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.30)",
      }}
    >
      <Svg width={16} height={16} viewBox="0 0 16 16" fill="none">
        <Path
          d="M3 6.2v3.6h2.5L12 12V4L5.5 6.2H3Z"
          stroke={AMBER_DEEP}
          strokeWidth={1.4}
          strokeLinejoin="round"
          fill="none"
        />
        <Path d="M5.5 9.8V12c0 .6.4 1 1 1H7c.6 0 1-.4 1-1v-1" stroke={AMBER_DEEP} strokeWidth={1.4} fill="none" />
      </Svg>
    </View>
  );
}

function BellGlyph() {
  return (
    <View
      style={{
        width: 30,
        height: 30,
        borderRadius: 6,
        backgroundColor: "rgba(143,187,138,0.14)",
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: "rgba(143,187,138,0.30)",
      }}
    >
      <Svg width={15} height={15} viewBox="0 0 16 16" fill="none">
        <Path
          d="M4 7a4 4 0 0 1 8 0c0 3.5 1.2 4.6 1.6 5H2.4C2.8 11.6 4 10.5 4 7Z"
          stroke="#5A7440"
          strokeWidth={1.4}
          strokeLinejoin="round"
          fill="none"
        />
        <Path d="M6.5 13.4a1.5 1.5 0 0 0 3 0" stroke="#5A7440" strokeWidth={1.4} strokeLinecap="round" fill="none" />
      </Svg>
    </View>
  );
}

// Keep Rect referenced so the editor doesn't strip the import on later edits.
void Rect;
